package com.ives.modules;

import com.ives.utils.math.Vector3D;
import com.ives.utils.math.MathStats;
import js.html.DivElement;
import js.html.TouchEvent;
import js.html.Element;
import haxe.PosInfos;
import haxe.ds.StringMap;

import js.html.MouseEvent;
import js.html.svg.PathElement;
import js.html.svg.CircleElement;
import js.html.svg.SVGElement;
import js.Browser;

import com.er453r.hxcompo.Component;

import com.ives.modules.measurements.MeasurementUtils;
import com.ives.utils.math.Vector2D;
import com.ives.components.workspace.DicomWorkspace;
import com.ives.components.viewer.DicomViewer;
import com.ives.modules.Drawing;
import com.ives.modules.DrawingHandle;
import com.ives.modules.ModuleEvent;
import com.ives.utils.Log;

@:abstract
class Module extends Component{
	private static inline var HANDLE_SIZE:Float = 8;
	private static inline var DOUBLE_CLICK_TIME:Float = 1;
	
	private static inline var MAX_DRAWING_DISTANCE_FROM_SLICE:Float = 0.1; // 0.1 mm, should be accurate enough

	private static inline var ALWAYS_DRAW:String = "ALWAYS_DRAW"; // some drawings are special and can be drawn always - MPR line for example

	public var dicomWorkspace:DicomWorkspace;
	public var dicomViewer:DicomViewer;
	public var workspaceModule:Bool = true;

	private var drawings:StringMap<Drawing> = new StringMap<Drawing>();
	private var selectedHandle:DrawingHandle;
	private var closeHandle:DrawingHandle;

	private var svg:SVGElement = cast Browser.document.createElementNS("http://www.w3.org/2000/svg", "svg");
	private var infoContainer:DivElement = cast Browser.document.createDivElement();

	public function init(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		this.dicomViewer = dicomViewer;
		this.dicomWorkspace = dicomWorkspace;

		svg.classList.add("fill");
		viewElement.appendChild(svg);

		infoContainer.classList.add("infoContainer");
		viewElement.appendChild(infoContainer);

		svg.setAttribute("width", "100%");
		svg.setAttribute("height", "100%");
	}

	private function addDrawing(drawing:Drawing){
		drawings.set(drawing.id, drawing);

		redraw();
	}

	private function removeDrawing(id:String){
		drawings.remove(id);

		redraw();
	}

	public function handleModuleEvent(event:ModuleEvent){

	}

	private function dispatchEvent(event:ModuleEvent, ?pos : PosInfos){
		event.origin = '${pos.fileName}:${pos.lineNumber} (${pos.methodName})';

		dispatch("moduleevent", event);
	}

	private function redraw(){
		while(svg.firstChild != null)
			svg.removeChild(svg.firstChild);

		while(infoContainer.firstChild != null)
			infoContainer.removeChild(infoContainer.firstChild);

		if(dicomWorkspace.dicomImage.position == null)
			return;

		for(drawing in drawings.iterator()){
			if(drawing.type == Drawing.SHAPE){
				var points3d = floatsToPoints3D(drawing.points);

				if(dicomWorkspace.dicomImage.columnDirection == null) // no slice info means we can not draw on the slice
					continue;

				if(drawing.id.indexOf("ALWAYS_DRAW") == -1){ // some drawings are special and can be drawn always - MPR line for example
					var maxDistance:Float = -1; // distanceFromSlice is always positive

					for(point in points3d){
						var distance = dicomWorkspace.dicomImage.distanceFromSlice(point);

						if(distance > maxDistance)
							maxDistance = distance;
					}

					if(maxDistance > MAX_DRAWING_DISTANCE_FROM_SLICE)
						continue;

					Log.debug('Max distance for measurement is ${maxDistance} mm. on workspace ${dicomWorkspace.mode}}');
				}

				var localPoints:Array<Vector2D> = volumeToLocal(points3d);
				var points:Array<Float> = pointsToFloats2D(localPoints);

				infoContainer.appendChild(drawing.measurementInfo.viewElement);
				drawing.measurementInfo.setLocalPosition(MathStats.averagePoints(localPoints));

				var limit:UInt = points.length - 3;

				if(drawing.closed)
					limit = points.length - 1;

				var n:UInt = 0;

				if(drawing.event != null)
					drawing.measurementInfo.viewElement.classList.add("show");

				var highlightedDrawing:Bool = false;

				for(handle in drawing.handles)
					if(handle == closeHandle)
						highlightedDrawing = true;

				while(n < limit){
					var path:PathElement = MeasurementUtils.drawMeasuredLine(points[n], points[n + 1], points[(n + 2) % points.length], points[(n + 3) % points.length], drawing.showScale, 10);

					if(drawing.inProgress || highlightedDrawing)
						path.setAttribute("stroke", "#6caabb");
					else
						path.setAttribute("stroke", drawing.color);

					path.setAttribute("stroke-width", "3");
					path.classList.add("shadow");

					svg.appendChild(path);

					n += 2;
				}

				if(drawing.onModify != null){
					n = 0;

					var localHandles:Array<Float> = pointsToFloats2D(volumeToLocal(floatsToPoints3D(drawing.handlePoints)));

					for(handle in drawing.handles){
						var className:String = "handle";

						if(handle == closeHandle)
							className = "handleClose";

						if(selectedHandle != null && handle.index == selectedHandle.index)
							className = "handleSelected";

						if(drawing.inProgress){
							className = "handleInProgress";
							drawing.measurementInfo.viewElement.classList.add("show");
						}

						var circle:CircleElement = cast Browser.document.createElementNS("http://www.w3.org/2000/svg", "circle");

						circle.setAttribute("class", className);
						circle.cx.baseVal.value = localHandles[n];
						circle.cy.baseVal.value = localHandles[n + 1];
						circle.r.baseVal.value = HANDLE_SIZE;

						if(drawing.inProgress)
							circle.setAttribute("stroke", "#6caabb");
						else
							circle.setAttribute("stroke", drawing.color);

						circle.setAttribute("stroke-width", "2");

						svg.appendChild(circle);

						n += 2;
					}
				}
			}
		}
	}

	public function onDrawingMouseDown(x:Float, y:Float, z:Float):Bool{
		for(drawing in drawings)
			if(drawing.inProgress)
				return false; // if ANY drawing is in progress - accept all clicks

		for(drawing in drawings)
			if(!drawing.inProgress)
				for(handle in drawing.handles)
					if(handle.distanceFromLessThan(x, y, z, (1.5 * HANDLE_SIZE / dicomWorkspace.dicomImage.scale) * dicomWorkspace.dicomImage.columnSpacing)){
						selectedHandle = handle;

						Log.debug("Select handle mouse down BLOCK");

						return true;
					}

		Log.debug("Select handle mouse down NO block");

		return false;
	}

	public function onDrawingMouseUp(x:Float, y:Float, z:Float):Bool{
		if(selectedHandle != null){
			Log.debug('Dropping selected handle');

			selectedHandle = null;

			return true;
		}

		return false;
	}

	public function onDrawingMouseMove(x:Float, y:Float, z:Float):Bool{
		for(drawing in drawings)
			if(drawing.inProgress)
				return false; // if any drawing is in progress, leaeve other drawings alone

		if(selectedHandle != null){
			Log.debug('Handle selected, moving drawing...');

			selectedHandle.drawing.lastHandlePosition = [selectedHandle.drawing.handlePoints[selectedHandle.index], selectedHandle.drawing.handlePoints[selectedHandle.index + 1]];

			selectedHandle.drawing.handlePoints[selectedHandle.index] = x;
			selectedHandle.drawing.handlePoints[selectedHandle.index + 1] = y;
			selectedHandle.drawing.handlePoints[selectedHandle.index + 2] = z;

			selectedHandle.drawing.lastHandleIndex = selectedHandle.index;
			selectedHandle.drawing.onModify(selectedHandle.drawing);

			return true;
		}
		else{
			var previousCloseHandle:DrawingHandle = closeHandle;

			var redrawNeeded:Bool = (closeHandle != null);

			closeHandle = null;

			for(drawing in drawings)
				for(handle in drawing.handles)
					if(handle.distanceFromLessThan(x, y, z, (1.5 * HANDLE_SIZE / dicomWorkspace.dicomImage.scale) * dicomWorkspace.dicomImage.columnSpacing)){
						closeHandle = handle;
						redrawNeeded = true;
					}

			if(redrawNeeded)
				redraw();
		}

		return false;
	}

	private function floatsToPoints2D(floats:Array<Float>):Array<Vector2D>{
		var vectors:Array<Vector2D> = [];

		var n = 0;

		while(n < floats.length)
			vectors.push( new Vector2D(floats[n++], floats[n++]) );

		return vectors;
	}

	private function floatsToPoints3D(floats:Array<Float>):Array<Vector3D>{
		var vectors:Array<Vector3D> = [];

		var n = 0;

		while(n < floats.length)
			vectors.push( new Vector3D(floats[n++], floats[n++], floats[n++]) );

		return vectors;
	}

	private function pointsToFloats2D(points:Array<Vector2D>):Array<Float>{
		var floats:Array<Float> = [];

		for(n in 0...points.length){
			floats.push(points[n].x);
			floats.push(points[n].y);
		}

		return floats;
	}

	private function pointsToFloats3D(points:Array<Vector3D>):Array<Float>{
		var floats:Array<Float> = [];

		for(n in 0...points.length){
			floats.push(points[n].x);
			floats.push(points[n].y);
			floats.push(points[n].z);
		}

		return floats;
	}

	private function volumeToLocal(?imagePoints:Array<Vector3D>):Array<Vector2D>{
		var localPoints:Array<Vector2D> = [];

		for(imagePoint in imagePoints){
			var localPoint:Vector2D = dicomWorkspace.dicomImage.getImageCoordinates(imagePoint.clone());

			localPoint.scale(dicomWorkspace.dicomImage.scale);
			localPoint.rotate(dicomWorkspace.dicomImage.rotation);
			localPoint.increment(new Vector2D(dicomWorkspace.dicomImage.x, dicomWorkspace.dicomImage.y));

			localPoints.push(localPoint);
		}

		return localPoints;
	}

	private function imageToLocal(?imagePoints:Array<Vector2D>):Array<Vector2D>{
		var localPoints:Array<Vector2D> = [];

		for(imagePoint in imagePoints){
			var localPoint:Vector2D = imagePoint.clone();

			localPoint.scale(dicomWorkspace.dicomImage.scale);
			localPoint.rotate(dicomWorkspace.dicomImage.rotation);
			localPoint.increment(new Vector2D(dicomWorkspace.dicomImage.x, dicomWorkspace.dicomImage.y));

			localPoints.push(localPoint);
		}

		return localPoints;
	}

	private function volumeToLocalPoint(?x:Float, ?y: Float, ?z: Float, ?point:Vector3D):Vector2D{
		if(point != null)
			return volumeToLocal([point]).pop();

		if(x != null && y != null && z != null)
			return volumeToLocal([new Vector3D(x, y, z)]).pop();

		return null;
	}

	private function volumeToImagePoint(volumePoint:Vector3D):Vector2D{
		return dicomWorkspace.dicomImage.getImageCoordinates(volumePoint);
	}

	private function volumeToImage(volumePoints:Array<Vector3D>):Array<Vector2D>{
		var imagePoints:Array<Vector2D> = [];

		for(volumePoint in volumePoints)
			imagePoints.push(dicomWorkspace.dicomImage.getImageCoordinates(volumePoint));

		return imagePoints;
	}

	private function localToVolume(localPoints:Array<Vector2D>):Array<Vector3D>{
		var imagePoints:Array<Vector3D> = [];

		for(localPoint in localPoints){
			var imagePoint:Vector2D = localPoint.clone();

			imagePoint.decrement(new Vector2D(dicomWorkspace.dicomImage.x, dicomWorkspace.dicomImage.y));
			imagePoint.scale(1 / dicomWorkspace.dicomImage.scale);
			imagePoint.rotate(-dicomWorkspace.dicomImage.rotation);

			imagePoints.push(dicomWorkspace.dicomImage.getDicomCoordinates(imagePoint));
		}

		return imagePoints;
	}

	private function imageToVolume(localPoints:Array<Vector2D>):Array<Vector3D>{
		var imagePoints:Array<Vector3D> = [];

		for(localPoint in localPoints){
			var imagePoint:Vector2D = localPoint.clone();

			imagePoints.push(dicomWorkspace.dicomImage.getDicomCoordinates(imagePoint));
		}

		return imagePoints;
	}

	private function localToImage(localPoints:Array<Vector2D>):Array<Vector2D>{
		var imagePoints:Array<Vector2D> = [];

		for(localPoint in localPoints){
			var imagePoint:Vector2D = localPoint.clone();

			imagePoint.decrement(new Vector2D(dicomWorkspace.dicomImage.x, dicomWorkspace.dicomImage.y));
			imagePoint.scale(1 / dicomWorkspace.dicomImage.scale);
			imagePoint.rotate(-dicomWorkspace.dicomImage.rotation);

			imagePoints.push(imagePoint);
		}

		return imagePoints;
	}

	private function volumeToLocalFloats(points:Array<Float>):Array<Float>{
		return pointsToFloats2D(volumeToLocal(floatsToPoints3D(points)));
	}

	private function localToVolumeFloats(points:Array<Float>):Array<Float>{
		return pointsToFloats3D(localToVolume(floatsToPoints2D(points)));
	}

	private function localToImageFloats(points:Array<Float>):Array<Float>{
		return pointsToFloats2D(localToImage(floatsToPoints2D(points)));
	}

	public function clone():Module{
		return cast Type.createInstance(Type.getClass(this), []);
	}

	private function getWorkspaceMode():String{
		return dicomWorkspace.mode;
	}

	private function workspaceIdForEvent():Int{
		if(dicomWorkspace.dicomViewer.allWorkspaces.checked)
			return -1;
		else
			return dicomWorkspace.id;
	}

	private function getOffset():Vector2D{
		var offset:Vector2D = new Vector2D();

		var node:Element = dicomWorkspace.viewElement;

		while(node != null){
			offset.x += node.offsetLeft;
			offset.y += node.offsetTop;

			node = node.offsetParent;
		}

		return offset;
	}

	private function mouseToVolume(event:MouseEvent):Vector3D{
		var offset:Vector2D = getOffset();

		var imagePoint:Array<Vector3D> = localToVolume([new Vector2D(event.pageX - offset.x, event.pageY - offset.y)]);

		return imagePoint[0];
	}

	private function touchToVolume(event:TouchEvent):Vector3D{
		var offset:Vector2D = getOffset();

		var imagePoint:Array<Vector3D> = localToVolume([new Vector2D(event.touches.item(0).pageX - offset.x, event.touches.item(0).pageY - offset.y)]);

		return imagePoint[0];
	}

	private function hide(element:Element){
		element.style.display = "none";
	}

	private function show(element:Element){
		element.style.display = "";
	}

	private function hasModifier(event:MouseEvent):Bool{
		if(event == null)
			return false;

		return (event.altKey || event.shiftKey || event.ctrlKey || event.metaKey);
	}
}
